import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { RefreshCw, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BuildsPage() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchRuns = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/builds?limit=50`);
      setRuns(response.data.runs || []);
    } catch (error) {
      console.error('Failed to fetch build runs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRuns();
  }, []);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle size={12} className="mr-1" /> Success</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700"><XCircle size={12} className="mr-1" /> Failed</Badge>;
      case 'running':
        return <Badge className="bg-blue-100 text-blue-700"><Loader2 size={12} className="mr-1 animate-spin" /> Running</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatTime = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString();
  };

  const openDetail = (run) => {
    setSelectedRun(run);
    setDetailOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="builds-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Build History</h1>
          <p className="text-gray-500 text-sm">Audit trail for site builds</p>
        </div>
        <Button variant="outline" onClick={fetchRuns} disabled={loading}>
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock size={18} />
            Recent Builds ({runs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          ) : runs.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No build runs yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium">Run ID</th>
                    <th className="pb-2 font-medium">Lead ID</th>
                    <th className="pb-2 font-medium">Mode</th>
                    <th className="pb-2 font-medium">Status</th>
                    <th className="pb-2 font-medium">Duration</th>
                    <th className="pb-2 font-medium">Templates</th>
                    <th className="pb-2 font-medium">Missing</th>
                    <th className="pb-2 font-medium">Started</th>
                    <th className="pb-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr 
                      key={run.run_id} 
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => openDetail(run)}
                    >
                      <td className="py-3 font-mono text-xs">{run.run_id}</td>
                      <td className="py-3 font-mono text-xs">{run.lead_id?.substring(0, 8)}...</td>
                      <td className="py-3">
                        <Badge variant={run.mode === 'FINAL' ? 'default' : 'outline'}>
                          {run.mode}
                        </Badge>
                      </td>
                      <td className="py-3">{getStatusBadge(run.status)}</td>
                      <td className="py-3">{formatDuration(run.duration_ms)}</td>
                      <td className="py-3">v{run.prompt_templates_version || '-'}</td>
                      <td className="py-3">
                        {run.missing_assets_count > 0 ? (
                          <span className="text-amber-600">{run.missing_assets_count}</span>
                        ) : (
                          <span className="text-green-600">0</span>
                        )}
                      </td>
                      <td className="py-3 text-xs text-gray-500">{formatTime(run.started_at)}</td>
                      <td className="py-3">
                        <Button variant="ghost" size="sm">View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Build Run Details</DialogTitle>
          </DialogHeader>
          {selectedRun && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-500">Run ID</span>
                  <p className="font-mono">{selectedRun.run_id}</p>
                </div>
                <div>
                  <span className="text-gray-500">Status</span>
                  <p>{getStatusBadge(selectedRun.status)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Lead ID</span>
                  <p className="font-mono text-xs">{selectedRun.lead_id}</p>
                </div>
                <div>
                  <span className="text-gray-500">Mode</span>
                  <p>{selectedRun.mode}</p>
                </div>
                <div>
                  <span className="text-gray-500">Duration</span>
                  <p>{formatDuration(selectedRun.duration_ms)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Templates Version</span>
                  <p>v{selectedRun.prompt_templates_version || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Anchor Rewrites</span>
                  <p>{selectedRun.anchor_rewrites_count || 0}</p>
                </div>
                <div>
                  <span className="text-gray-500">Missing Assets</span>
                  <p className={selectedRun.missing_assets_count > 0 ? 'text-amber-600' : ''}>
                    {selectedRun.missing_assets_count || 0}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Started</span>
                  <p>{formatTime(selectedRun.started_at)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Finished</span>
                  <p>{formatTime(selectedRun.finished_at)}</p>
                </div>
              </div>
              {selectedRun.error_summary && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <span className="text-gray-500 text-xs">Error Summary</span>
                  <p className="text-red-700 text-xs font-mono mt-1">{selectedRun.error_summary}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
